import api from './api';
import type { InventoryBalance, InventoryMovement, MovementType, PaginatedResponse } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/inventory/movement`;

export interface ManualMovementPayload {
  inventoryItemId: string;
  /** The VenueLocation where this movement is posted. Required. */
  locationId: string;
  /** Phase 6: only MANUAL_ADJUSTMENT and WASTE are accepted. */
  type: Extract<MovementType, 'MANUAL_ADJUSTMENT' | 'WASTE'>;
  /** Signed integer. Positive = stock in, negative = stock out. Non-zero. */
  quantity: number;
  unitCost?: number;
  notes?: string;
}

/** Backend returns both the created movement and the updated balance in one response. */
export interface ManualMovementResult {
  movement: InventoryMovement;
  balance: InventoryBalance;
}

const inventoryMovementService = {
  /**
   * Get movements for a venue, optionally filtered by inventoryItemId, wineId, or type.
   * Always returned newest-first (postedAt DESC).
   */
  getMovements: async (
    venueId: string,
    page = 1,
    pageSize = 50,
    inventoryItemId?: string,
    wineId?: string,
    type?: MovementType,
  ): Promise<PaginatedResponse<InventoryMovement>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (inventoryItemId) params.inventoryItemId = inventoryItemId;
    if (wineId) params.wineId = wineId;
    if (type) params.type = type;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  /**
   * Post a manual adjustment or waste movement.
   * Returns both the created movement and the updated balance — no second round-trip needed.
   * RECEIVE, COUNT_VARIANCE, and TRANSFER movements are created by their own workflows.
   */
  postManualMovement: async (
    venueId: string,
    payload: ManualMovementPayload,
  ): Promise<ManualMovementResult> => {
    const response = await api.post(base(venueId), payload);
    return response.data;
  },
};

export default inventoryMovementService;
